import { useState } from "react";

import { Box, Button, TextField, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { NotificationSetting } from "types";

import { useSmartKeysMenu } from "../../../hooks/useSmartKeysMenu";
import { SmartKeyMenu } from "../../shared/SmartKeyMenu";
import EmailPreview from "./EmailPreview";
import EmailEditorDialog from "./EmailEditorDialog";
import MoovsTooltip from "components/MoovsTooltip";

type EmailInputWithSmartKeysProps = {
  notificationSetting: NotificationSetting;
  emailSubject: string;
  handleEmailSubjectChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleEmailSubmit: (
    design?: any,
    html?: string,
    fromEditor?: boolean
  ) => void;
  availableVariables: string[];
};

function EmailInputWithSmartKeys(props: EmailInputWithSmartKeysProps) {
  const {
    notificationSetting,
    emailSubject,
    handleEmailSubjectChange,
    handleEmailSubmit,
    availableVariables,
  } = props;

  // hooks
  const {
    menuProps,
    buttonProps,
    handleCopyToClipboard,
    notificationVariables,
  } = useSmartKeysMenu({
    availableVariables,
  });

  // state
  const [editorDialogOpen, setEditorDialogOpen] = useState(false);

  // derived state
  const canEditTemplate = notificationSetting.emailEditorProps;

  const handleSaveTemplate = (design: any, html: string) => {
    // Call parent's handleEmailSubmit with design and html from the editor
    // Pass fromEditor=true to indicate this save is from the email editor
    handleEmailSubmit(design, html, true);
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <MoovsTooltip tooltipText="Smart Keys are useable in the email subject only.">
          <Button {...buttonProps} id="basic-button">
            + Smart Keys
          </Button>
        </MoovsTooltip>
      </Box>

      <SmartKeyMenu
        {...menuProps}
        notificationVariables={notificationVariables}
        onCopyToClipboard={handleCopyToClipboard}
        helpText="Add smart keys to your email subject to add personalized details. To use them, just copy and paste them into the subject field."
      />

      {/* SMS Customized Content */}
      <Box>
        {/* Email Subject */}
        <Box mb={2}>
          <Typography variant="h4">Subject</Typography>
        </Box>
        <TextField
          rows={1}
          fullWidth
          variant="outlined"
          value={emailSubject}
          onChange={handleEmailSubjectChange}
        />

        {/* Email HTML Preview */}
        {canEditTemplate && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              mt={3}
            >
              <Typography variant="h4">Email Preview</Typography>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditorDialogOpen(true)}
                size="small"
              >
                Edit Template
              </Button>
            </Box>
            <EmailPreview
              htmlContent={notificationSetting.emailEditorProps.previewHtml}
            />
            <EmailEditorDialog
              open={editorDialogOpen}
              onClose={() => setEditorDialogOpen(false)}
              notificationSetting={notificationSetting}
              onSave={handleSaveTemplate}
            />
          </>
        )}
      </Box>
    </>
  );
}

export default EmailInputWithSmartKeys;
